import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { parsePhoneNumberFromString, getExampleNumber } from "libphonenumber-js/max";
import examples from "libphonenumber-js/mobile/examples";
import { api, formatApiError } from "@/lib/api";
import { WAITLIST } from "@/constants/testIds/waitlist";

const CODES = [
  ["+91", "India", "IN"], ["+1", "USA / Canada", "US"], ["+44", "UK", "GB"], ["+971", "UAE", "AE"],
  ["+966", "Saudi Arabia", "SA"], ["+974", "Qatar", "QA"], ["+965", "Kuwait", "KW"], ["+968", "Oman", "OM"],
  ["+973", "Bahrain", "BH"], ["+65", "Singapore", "SG"], ["+60", "Malaysia", "MY"], ["+61", "Australia", "AU"],
  ["+64", "New Zealand", "NZ"], ["+977", "Nepal", "NP"], ["+94", "Sri Lanka", "LK"], ["+880", "Bangladesh", "BD"],
  ["+92", "Pakistan", "PK"], ["+49", "Germany", "DE"], ["+33", "France", "FR"], ["+27", "South Africa", "ZA"],
];
const REGION = Object.fromEntries(CODES.map(([c, , r]) => [c, r]));
const MOBILE = ["MOBILE", "FIXED_LINE_OR_MOBILE"];

const isMobile = (cc, num) => {
  const p = parsePhoneNumberFromString(`${cc}${num.replace(/\D/g, "").replace(/^0+/, "")}`);
  return !!p && p.isValid() && MOBILE.includes(p.getType());
};
const example = (cc) =>
  getExampleNumber(REGION[cc], examples)?.formatInternational().replace(`${cc} `, "") || "Mobile number";

const field = "h-12 bg-white border-border rounded-md text-base focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2";

export const WaitlistForm = ({ onSuccess }) => {
  const [f, setF] = useState({ name: "", email: "", country_code: "+91", whatsapp: "", consent: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!f.email.trim()) return setError("Please enter your email.");
    if (f.whatsapp.trim() && !isMobile(f.country_code, f.whatsapp))
      return setError(`Please enter a valid mobile number for ${f.country_code}, e.g. ${example(f.country_code)}.`);
    if (!f.consent) return setError("Please tick the box so we can contact you about the launch.");
    setBusy(true);
    try {
      const { data } = await api.post("/waitlist", { ...f, email: f.email.trim() || null, whatsapp: f.whatsapp.trim() || null });
      onSuccess(data.name);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const item = (i) => ({ initial: { opacity: 0, y: 14 }, animate: { opacity: 1, y: 0 }, transition: { delay: 0.35 + i * 0.07, duration: 0.5 } });

  return (
    <form onSubmit={submit} data-testid={WAITLIST.form} className="space-y-5 max-w-md">
      <motion.div {...item(0)} className="space-y-2">
        <Label htmlFor="name">Your name</Label>
        <Input id="name" required value={f.name} onChange={(e) => set("name")(e.target.value)}
          placeholder="Full name" className={field} data-testid={WAITLIST.nameInput} />
      </motion.div>
      <motion.div {...item(1)} className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input id="email" type="email" required value={f.email} onChange={(e) => set("email")(e.target.value)}
          placeholder="you@example.com" className={field} data-testid={WAITLIST.emailInput} />
      </motion.div>
      <motion.div {...item(2)} className="space-y-2">
        <Label htmlFor="whatsapp">WhatsApp number <span className="text-muted-foreground font-normal">(optional)</span></Label>
        <div className="flex gap-2">
          <Select value={f.country_code} onValueChange={set("country_code")}>
            <SelectTrigger className="h-12 w-[112px] bg-white text-base" data-testid={WAITLIST.countryCodeSelect} aria-label="Country code">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CODES.map(([c, n]) => (
                <SelectItem key={c} value={c} data-testid={`waitlist-country-option-${c.slice(1)}`}>
                  {c} <span className="text-muted-foreground ml-1">{n}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input id="whatsapp" type="tel" inputMode="numeric" value={f.whatsapp}
            onChange={(e) => set("whatsapp")(e.target.value.replace(/[^\d\s-]/g, ""))}
            placeholder={example(f.country_code)} maxLength={17} className={`${field} flex-1`} data-testid={WAITLIST.whatsappInput} />
        </div>
      </motion.div>
      <motion.label {...item(3)} htmlFor="consent" className="flex items-start gap-3 text-sm text-muted-foreground cursor-pointer leading-relaxed">
        <Checkbox id="consent" checked={f.consent} onCheckedChange={(v) => set("consent")(v === true)}
          className="mt-0.5 data-[state=checked]:bg-primary data-[state=checked]:border-primary" data-testid={WAITLIST.consentCheckbox} />
        I'm happy to hear from Archana Relationship School by email and WhatsApp about the launch.
      </motion.label>
      {error && <p role="alert" className="text-sm text-destructive" data-testid={WAITLIST.formError}>{error}</p>}
      <motion.div {...item(4)}>
        <Button type="submit" disabled={busy} data-testid={WAITLIST.submitButton}
          className="btn-lift h-12 w-full sm:w-auto px-8 rounded-md text-base bg-primary hover:bg-primary/90">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Join the waitlist <ArrowRight className="h-4 w-4 ml-1" /></>}
        </Button>
      </motion.div>
    </form>
  );
};
